'use client';

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@mokoboko/components';

import {
  getCheckoutAcknowledgments,
  getTermsAcceptanceKey,
  getTermsTagValues,
  isTermsAcceptanceError,
  termsRefusalMessage,
  toAcceptedTerms,
  TERMS_REQUIRED_MESSAGE,
} from '@/helpers/terms';
import type { TermsScope } from '@/services/terms';
import { useAuth } from '@/contexts/auth';
import useResolvedTerms, { TERMS_RESOLVE_QUERY_KEY } from '@/state/terms';
import useTermsAcceptanceStatuses, {
  TERMS_ACCEPTANCE_STATUS_QUERY_KEY,
} from '@/state/terms-acceptance-status';
import useTenant from '@/state/tenant';

// Acceptance is keyed on document ids + versions, so a version bump published
// mid-session brings the checkbox back and forces re-acceptance.
export const useTermsAcceptance = ({
  type,
  checkAccepted = true,
  entityIds,
  billingInterval,
  requiredMessage = TERMS_REQUIRED_MESSAGE,
  changedMessage,
  asksRequest = true,
}: {
  type: TermsScope;
  checkAccepted?: boolean;
  entityIds: string[];
  billingInterval?: string | null;
  requiredMessage?: string;
  // Replaces the changed terms sentence a refusal of stale or missing terms
  // shows.
  changedMessage?: string;
  // False where nothing is bought, so there is no purchase to start early: a
  // gift card recipient redeeming the buyer's card.
  asksRequest?: boolean;
}) => {
  const [acceptedKey, setAcceptedKey] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(0);
  // A mid-session terms rejection must bring the checkbox back so the customer
  // re-consents to the changed version instead of being stuck on the
  // agreement line with no way to accept.
  const [mustReaccept, setMustReaccept] = useState(false);
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const canCheckAccepted = checkAccepted && isAuthenticated;
  const { data: tenant } = useTenant();
  const { docs, isLoading, isError, refetch } = useResolvedTerms({
    type,
    entityIds,
  });
  const { acceptedVersions, isFetching: isAcceptanceFetching } =
    useTermsAcceptanceStatuses({
      docIds: docs.map((doc) => doc.terms_document_id),
      enabled: canCheckAccepted,
    });

  const acknowledgments = asksRequest
    ? getCheckoutAcknowledgments(type, docs)
    : [];

  const alreadyAccepted =
    docs.length > 0 &&
    docs.every((doc) => {
      const version = acceptedVersions[doc.terms_document_id];
      return version != null && version >= doc.version;
    });

  // An acknowledgment is an express request for this order, so it is asked for
  // again on every purchase even once the terms themselves are accepted.
  const showCheckbox =
    docs.length > 0 &&
    (mustReaccept || acknowledgments.length > 0 || !alreadyAccepted);

  const termsKey = getTermsAcceptanceKey(docs);
  const checked = showCheckbox && acceptedKey === termsKey;
  const tagValues = useMemo(
    () => getTermsTagValues(tenant, billingInterval),
    [tenant, billingInterval]
  );

  const onChange = (value: boolean) => {
    setAcceptedKey(value ? termsKey : null);
    setError('');
  };

  const requireAccepted = () => {
    const accepted = !showCheckbox || checked;
    setError(accepted ? '' : requiredMessage);
    if (!accepted) setShake((s) => s + 1);
    return accepted;
  };

  const buildPayload = () =>
    docs.length ? { accepted_terms: toAcceptedTerms(docs) } : {};

  const handleSubmitError = (submitError: unknown) => {
    if (!isTermsAcceptanceError(submitError)) return false;
    const message = termsRefusalMessage(changedMessage);
    setAcceptedKey(null);
    setMustReaccept(true);
    setError(message);
    // mustReaccept puts the checkbox, and the message under it, on screen
    // whenever there is a document to agree to.
    if (!docs.length) toast.error(message);
    queryClient.invalidateQueries({ queryKey: [TERMS_RESOLVE_QUERY_KEY] });
    queryClient.invalidateQueries({
      queryKey: [TERMS_ACCEPTANCE_STATUS_QUERY_KEY],
    });
    return true;
  };

  // Hold the terms UI until the fresh acceptance answer lands, so a returning
  // customer never sees the checkbox flicker into the agreement line. After a
  // refusal the checkbox shows whatever that answer says, so the refetch the
  // refusal starts holds nothing.
  const isResolving =
    isLoading ||
    (canCheckAccepted &&
      docs.length > 0 &&
      isAcceptanceFetching &&
      !mustReaccept);

  return {
    docs,
    isLoading: isResolving,
    isError,
    refetch,
    checked,
    onChange,
    error,
    shake,
    acknowledgments,
    showCheckbox,
    tagValues,
    requireAccepted,
    buildPayload,
    handleSubmitError,
  };
};
